import { createSlice } from '@reduxjs/toolkit';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { ChecklistItem } from 'pages/Checklist/types';
import { QuestionData } from 'pages/ConductInspection/components/ChecklistDetails/ChecklistDetails';
import { SubTask, Task, Team } from './projectSlice';

export interface IInspection {
  projectId: string;
  displayId: string;
  created_at?: string;
  updated_at?: string;
  subProject?: { name: string; _id?: string };
  subTask?: { name: string; _id?: string };
  endTask?: { name: string; _id?: string };
  checklistInfo: {
    checklistId: string;
    libraryId: string;
    checklistName?: string;
    subChecklistName?: string;
  };
  response?: Record<string, QuestionData>;
  scheduledDate?: Date;
  status: WorkFlowStatus;
  selectedDoer?: string;
  inspector?: string;
  submissionDocuments?: { docType: string; url: string }[];
  drawingTitle?: { docType: string; url: string }[];
  doerImages?: { docType: string; url: string }[];
  generalFeedback?: string;
  satisfactionLevel?: number;
  eSign?: string;
  doer: string;
  _id: string;
  locations: string[];
  rescheduleReason?: string;
  isCancelled?: boolean;
  cancelledBy?: string;
  cancelReason?: string;
  cancelDate?: Date;
  inspectionDate?: Date;
}

export type ProjectDetails = {
  _id?: string;
  subProject: Task;
  subTask: SubTask;
  endTask: { name: string };
  locations: string[];
  selectedChecklist: ChecklistItem;
  selectedDoer: Team;
  selectedInspector: Team;
  scheduledTime?: string;
};

export type SubmissionDetalis = {
  submissionDocuments: { docType: string; url: string }[];
  drawingTitle: { docType: string; url: string }[];
  doerImages: { docType: string; url: string }[];
  generalFeedback: string;
  satisfactionLevel: number;
  eSign: string;
  status?: WorkFlowStatus;
  closeByDate?: string;
};

export type Document = {
  _id: string;
  docType: string;
  url: string;
};

export enum commentActionEnum {
  Approve = 'APPROVE',
  Disapprove = 'DISAPPROVE',
  Comment = 'COMMENT',
  SendForApproval = 'SEND_FOR_APPROVAL',
  TaskRaised = 'TASK_RAISED',
  Scheduled = 'SCHEDULED',
  Closed = 'CLOSED',
}

export type Comment = {
  _id: string;
  action: commentActionEnum;
  senderId: string;
  senderRole: string;
  comment: string;
  approved: boolean;
  commentedAt: string;
  documents: Document[];
};

export type QuestionInfo = {
  _id: string;
  question: string;
  response: 'NO' | 'YES' | 'NA';
  comments: Comment[];
  requireReinspection: boolean;
  documents: Document[];
};

export type InspectionTask = {
  _id: string;
  name: string;
  projectId: string;
  userId: string;
  inspector: string;
  doer: string;
  inspectionId: string;
  questionId: string;
  status: WorkFlowStatus;
  type: string;
  created_at: string;
  updated_at: string;
  questionInfo: QuestionInfo;
  currentStatus: commentActionEnum;
  updatedBy: string;
};

export interface IInspectionReport {
  projectInfo: {
    projectDisplayId: string;
    projectName: string;
  };
  tasks: InspectionTask[];
  inspectionInfo: IInspection;
}

export interface ConductInspectionInitialStates {
  projectDetails: ProjectDetails | null;
  checklistDetails: {
    questions: Record<string, QuestionData>;
  };
  submissionDetails: SubmissionDetalis | null;
}

const initialState: ConductInspectionInitialStates = {
  projectDetails: null,
  checklistDetails: {
    questions: {},
  },
  submissionDetails: null,
};

const conductInspectionSlice = createSlice({
  name: 'conductInspectionSlice',
  initialState,
  reducers: {
    setProjectDetails: (state, { payload }) => {
      state.projectDetails = payload;
    },

    setChecklistDetails: (state, { payload }) => {
      state.checklistDetails.questions = payload;
    },
    setSubmissionDetails: (state, { payload }) => {
      state.submissionDetails = payload;
    },
  },
});

export const { setProjectDetails, setChecklistDetails, setSubmissionDetails } =
  conductInspectionSlice.actions;
export default conductInspectionSlice.reducer;
